import type { TFunction } from "i18next";
import { isAcceptableDateWindow, trailingWindowFromDaysBack } from "@nexus/contracts";
import type {
  AskNexusClarificationAnswer,
  AskNexusClarificationCard,
  AskNexusClarificationOption,
  AskNexusClarificationQuestion,
  AskNexusPeriodPreset,
} from "@/features/ask-nexus/api/ask-nexus.api";
import { formatBrandName } from "@/features/dashboard/lib/format";
import { platformDisplayName } from "@/features/dashboard/lib/my-brand-filter-options";

const LAST_30_DAYS = 30;

export interface PeriodChoice {
  preset: AskNexusPeriodPreset;
  startDate: string;
  endDate: string;
}

/** What the person has picked so far, by question id; nothing leaves the card until Continue. */
export type ClarificationDraft = Record<string, string[] | PeriodChoice | undefined>;

/** The period presets on the UTC calendar, resolved as the server's date context and the MCP presets resolve them. */
export function periodPresets(now: Date): Record<Exclude<AskNexusPeriodPreset, "custom">, { startDate: string; endDate: string }> {
  const today = trailingWindowFromDaysBack(1, now).endDate;
  return {
    month_to_date: { startDate: `${today.slice(0, 8)}01`, endDate: today },
    last_30_days: trailingWindowFromDaysBack(LAST_30_DAYS, now),
  };
}

/** A tool label wins; without one, brands and platforms are named the way the dashboard names them. */
export function optionLabel(
  t: TFunction,
  question: Extract<AskNexusClarificationQuestion, { kind: "choice" }>,
  option: AskNexusClarificationOption,
): string {
  if (option.label !== option.value) return option.label;
  if (question.optionKind === "brand") return formatBrandName(option.value);
  if (question.optionKind === "platform") return platformDisplayName(t, option.value);
  return option.label;
}

export function isPeriodComplete(period: PeriodChoice | undefined): period is PeriodChoice {
  return period !== undefined && isAcceptableDateWindow(period.startDate, period.endDate);
}

/** Required questions without an answer yet; Continue waits for none. */
export function missingRequired(card: AskNexusClarificationCard, draft: ClarificationDraft): string[] {
  return card.questions.flatMap((question) => {
    if (!question.isRequired) return [];
    const value = draft[question.id];
    const isAnswered = question.kind === "period" ? isPeriodComplete(value as PeriodChoice | undefined) : Array.isArray(value) && value.length > 0;
    return isAnswered ? [] : [question.id];
  });
}

/** The answers sent with the reply, labels included so the answered card reads the same later. */
export function toAnswers(t: TFunction, card: AskNexusClarificationCard, draft: ClarificationDraft): AskNexusClarificationAnswer[] {
  return card.questions.flatMap((question): AskNexusClarificationAnswer[] => {
    if (question.kind === "period") {
      const period = draft[question.id] as PeriodChoice | undefined;
      return isPeriodComplete(period) ? [{ questionId: question.id, label: question.label, period }] : [];
    }
    const values = (draft[question.id] as string[] | undefined) ?? [];
    const choices = question.options
      .filter((option) => values.includes(option.value))
      .map((option) => ({ value: option.value, label: optionLabel(t, question, option) }));
    return [{ questionId: question.id, label: question.label, choices }];
  });
}

/** One line per question, as the person's reply shows in the chat and reaches the agent. */
export function answerLines(t: TFunction, answers: AskNexusClarificationAnswer[]): string[] {
  return answers.map((answer) => `${answer.label} ${answerValue(t, answer)}`);
}

export function answerValue(t: TFunction, answer: AskNexusClarificationAnswer): string {
  if ("period" in answer) {
    const range = t("askNexus.cards.clarification.range", "{{start}} to {{end}}", { start: answer.period.startDate, end: answer.period.endDate });
    return answer.period.preset === "custom" ? range : `${presetLabel(t, answer.period.preset)} (${range})`;
  }
  return answer.choices.length > 0
    ? answer.choices.map((choice) => choice.label).join(", ")
    : t("askNexus.cards.clarification.noPreference", "No preference");
}

export function presetLabel(t: TFunction, preset: AskNexusPeriodPreset): string {
  switch (preset) {
    case "month_to_date":
      return t("askNexus.cards.clarification.monthToDate", "This month to date");
    case "last_30_days":
      return t("askNexus.cards.clarification.last30Days", "Last 30 days");
    case "custom":
      return t("askNexus.cards.clarification.custom", "Custom range");
  }
}

/** The card as Copy gives it: each question with the options it offered. */
export function clarificationCopyLines(t: TFunction, card: AskNexusClarificationCard): string[] {
  return card.questions.flatMap((question) => {
    if (question.kind === "period") {
      return [question.label, `  ${t("askNexus.cards.clarification.periodOptions", "This month to date, Last 30 days, or a custom range")}`];
    }
    const shown = question.suggested.flatMap((value) => {
      const option = question.options.find((candidate) => candidate.value === value);
      return option ? [optionLabel(t, question, option)] : [];
    });
    return [
      question.label,
      `  ${t("askNexus.cards.clarification.optionsLine", "Options: {{options}} ({{total}} in all)", { options: shown.join(", "), total: question.total })}`,
    ];
  });
}
